/**
 * DIDAvatar — D-ID Live Streaming talking avatar.
 *
 * Connects to D-ID Streams via WebRTC, displays the real-time video
 * stream of the avatar speaking with perfect lip sync.
 *
 * Exposed via ref: { speak(text) }
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { didStream } from "@/lib/did";
import { cn } from "@/lib/cn";

export interface DIDAvatarHandle {
  speak: (text: string) => Promise<void>;
}

interface Props {
  sourceUrl?: string;
  speaking?: boolean;
  className?: string;
  onReady?: () => void;
  onSpeakStart?: () => void;
  onSpeakEnd?: () => void;
}

export const DIDAvatar = forwardRef<DIDAvatarHandle, Props>(
  ({ sourceUrl, speaking = false, className, onReady, onSpeakStart, onSpeakEnd }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [status, setStatus] = useState<"connecting" | "ready" | "error">("connecting");
    const [errorMsg, setErrorMsg] = useState<string>("");

    useImperativeHandle(ref, () => ({
      speak: (text: string) => didStream.speak(text),
    }));

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;
      let cancelled = false;

      (async () => {
        try {
          if (!didStream.isAvailable()) {
            throw new Error("D-ID not configured");
          }
          // Guard against React StrictMode double-mount AND any stale stream
          await didStream.disconnect().catch(() => {});
          if (cancelled) return;
          await didStream.connect(video, {
            sourceUrl,
            onSpeakStart,
            onSpeakEnd,
          });
          if (cancelled) {
            await didStream.disconnect().catch(() => {});
            return;
          }
          setStatus("ready");
          onReady?.();
        } catch (e) {
          console.error("D-ID connect error:", e);
          if (cancelled) return;
          const msg = e instanceof Error ? e.message : "Connection failed";
          if (msg.includes("Max user sessions")) {
            setErrorMsg("D-ID trial allows 1 stream at a time. Refresh the page to reset.");
          } else {
            setErrorMsg(msg);
          }
          setStatus("error");
        }
      })();

      return () => {
        cancelled = true;
        // Use beacon-safe cleanup so StrictMode unmount doesn't race
        void didStream.disconnect();
      };
    }, [sourceUrl]); // Only re-connect if avatar URL changes

    return (
      <div className={cn(
        "relative overflow-hidden rounded-2xl bg-gradient-to-b from-[#0c0920] via-[#110d26] to-[#0c0920]",
        className,
      )}>
        {/* Speaking glow */}
        <div className={cn(
          "pointer-events-none absolute inset-0 transition-opacity duration-500",
          speaking ? "opacity-50" : "opacity-10",
        )} style={{ background: "radial-gradient(ellipse 70% 60% at 50% 50%, #7c3aed 0%, transparent 75%)" }} />

        <video
          ref={videoRef}
          autoPlay
          playsInline
          controls={false}
          // Click anywhere on the video to unlock audio (Chrome autoplay fallback)
          onClick={() => {
            const v = videoRef.current;
            if (v) { v.muted = false; v.volume = 1.0; v.play().catch(() => {}); }
          }}
          className={cn(
            "h-full w-full object-cover transition-opacity duration-500 cursor-pointer",
            status === "ready" ? "opacity-100" : "opacity-0",
          )}
        />

        {status === "connecting" && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center text-white/60">
              <div className="text-3xl animate-spin mb-2">⚙</div>
              <div className="text-xs">Connecting to Maya…</div>
              <div className="text-[10px] mt-1 opacity-60">D-ID real-time stream</div>
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 grid place-items-center px-4">
            <div className="text-center text-red-400 text-xs space-y-3 max-w-[280px]">
              <div className="font-medium">Avatar stream failed</div>
              <div className="text-[10px] opacity-70">{errorMsg}</div>
              <button
                onClick={async () => {
                  setStatus("connecting");
                  setErrorMsg("");
                  await didStream.disconnect().catch(() => {});
                  await new Promise((r) => setTimeout(r, 1500));
                  const v = videoRef.current;
                  if (!v) return;
                  try {
                    await didStream.connect(v, { sourceUrl, onSpeakStart, onSpeakEnd });
                    setStatus("ready");
                    onReady?.();
                  } catch (e) {
                    setErrorMsg(e instanceof Error ? e.message : "Retry failed");
                    setStatus("error");
                  }
                }}
                className="rounded-full bg-white/10 hover:bg-white/20 px-4 py-1.5 text-xs text-white transition-colors"
              >
                ↻ Reset stream
              </button>
              <div className="text-[10px] text-white/40 pt-2">
                If "Max sessions" — D-ID free trial allows 1 stream. Wait 60s and click reset.
              </div>
            </div>
          </div>
        )}

        {status === "ready" && (
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
            <div className={cn(
              "flex items-center gap-2 rounded-full px-3 py-1.5 text-xs text-white/95 backdrop-blur-sm transition-colors duration-300",
              speaking ? "bg-primary/65" : "bg-black/45",
            )}>
              {speaking ? (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400" />
                  </span>
                  Maya is speaking…
                </>
              ) : (
                <><span className="h-1.5 w-1.5 rounded-full bg-white/40" /> Maya · GMAT Tutor</>
              )}
            </div>
            <div className="text-[9px] text-white/40 bg-black/30 rounded-full px-2 py-1 backdrop-blur-sm">
              D-ID Live · RunPod H100
            </div>
          </div>
        )}
      </div>
    );
  },
);
DIDAvatar.displayName = "DIDAvatar";
